import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Package, Truck, CheckCircle2, XCircle, Clock, ClipboardList } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { dashApi } from "../api";

const SAMPLE_STATUS = {
  pending:   { label: "Pending",   Icon: Clock,       cls: "bg-blue-50 text-blue-700 border-blue-200" },
  prepared:  { label: "Prepared",  Icon: Package,     cls: "bg-violet-50 text-violet-700 border-violet-200" },
  shipped:   { label: "Shipped",   Icon: Truck,       cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  delivered: { label: "Delivered", Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", Icon: XCircle,     cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const ORDER = ["pending", "prepared", "shipped", "delivered", "cancelled"];

/**
 * Sample-request fulfillment panel — shown on SubmissionDetailPage when the
 * submission is a `sample_request` shadow row. Loads the canonical record
 * from db.sample_requests via GET /api/dashboard/sample-requests/{sr_id}
 * and lets writers update status + tracking info.
 */
export default function SampleRequestPanel({ sampleRequestId, readOnly = false }) {
  const [sr, setSr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("");

  const load = useCallback(async () => {
    if (!sampleRequestId) return;
    setLoading(true);
    setErr("");
    try {
      const { data } = await dashApi.get(`/sample-requests/${sampleRequestId}`);
      setSr(data);
      setTracking(data.tracking_number || "");
      setCarrier(data.tracking_carrier || "");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load sample request");
    } finally {
      setLoading(false);
    }
  }, [sampleRequestId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (payload) => {
    setBusy(true);
    try {
      const { data } = await dashApi.patch(`/sample-requests/${sampleRequestId}`, payload);
      setSr(data);
      toast.success("Sample request updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-5 flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading sample request…
      </Card>
    );
  }
  if (err) return <Card className="p-5 text-sm text-rose-600">Sample request: {err}</Card>;
  if (!sr) return null;

  const contact = sr.contact || {};
  const products = sr.selectedProducts || [];
  const currentStatus = sr.status || "pending";
  const current = SAMPLE_STATUS[currentStatus] || SAMPLE_STATUS.pending;
  const CurrentIcon = current.Icon;

  return (
    <Card className="p-5 space-y-5" data-testid="sample-request-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <ClipboardList className="h-4 w-4" /> Sample Fulfillment
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Canonical record in <code className="font-mono">sample_requests</code>. Email
            notification to <code className="font-mono">samples@porticosystems.com</code> unchanged.
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${current.cls}`}
          data-testid="sample-status-badge"
        >
          <CurrentIcon className="h-3.5 w-3.5" /> {current.label}
        </span>
      </div>

      {/* Sample details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <Row label="Sample type"    value={sr.sampleType} />
        <Row label="Format"         value={sr.sampleFormat} />
        <Row label="Category"       value={sr.productCategory} />
        <Row label="Colors"         value={sr.colors} />
        <Row label="Quantity"       value={sr.quantity} />
        <Row label="Ship to"        value={[contact.address, contact.city, contact.state, contact.zip].filter(Boolean).join(", ")} />
      </div>

      {products.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Products</div>
          <div className="flex flex-wrap gap-1.5">
            {products.map((p) => (
              <span key={p} className="inline-block text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {sr.notes && (
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Notes</div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{sr.notes}</p>
        </div>
      )}

      {/* Fulfillment controls */}
      {!readOnly && (
        <div className="border-t border-slate-200 pt-4 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Update status</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {ORDER.map((s) => {
                const info = SAMPLE_STATUS[s];
                const Icon = info.Icon;
                const active = s === currentStatus;
                return (
                  <Button
                    key={s}
                    type="button"
                    size="sm"
                    variant={active ? "default" : "outline"}
                    disabled={busy || active}
                    onClick={() => patch({ status: s })}
                    data-testid={`sample-status-${s}`}
                  >
                    <Icon className="h-3.5 w-3.5 mr-1" /> {info.label}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="tracking-number" className="text-xs">Tracking number</Label>
              <Input
                id="tracking-number"
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                placeholder="1Z999AA10123456784"
                data-testid="sample-tracking-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tracking-carrier" className="text-xs">Carrier</Label>
              <Input
                id="tracking-carrier"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                placeholder="UPS / FedEx / USPS"
                data-testid="sample-carrier-input"
              />
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy || (tracking === (sr.tracking_number || "") && carrier === (sr.tracking_carrier || ""))}
            onClick={() => patch({ tracking_number: tracking, tracking_carrier: carrier })}
            data-testid="sample-save-tracking"
          >
            {busy ? "Saving…" : "Save tracking"}
          </Button>
        </div>
      )}
    </Card>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-xs uppercase tracking-wider text-slate-500 w-28 flex-shrink-0">{label}</span>
    <span className="text-sm text-slate-900">{value || <span className="text-slate-400">—</span>}</span>
  </div>
);
